/**
 * Utility functions for articleEditor components
 */

/**
 * Parse EEAT data from a string or direct object
 * @param data The data to parse (string or object)
 * @returns Parsed EEAT data object
 */
export const parseEEATData = (data: any) => {
    // If nothing to parse, return null
    if (!data) {
        return null;
    }

    // If it's already an object with a string property articleEEATScore
    if (data && typeof data === 'object' && typeof data.articleEEATScore === 'string') {
        try {
            // Parse the articleEEATScore string
            const parsed = JSON.parse(data.articleEEATScore);
            return parseEEATData(parsed); // Recursively parse the result
        } catch (error) {
            console.error("Error parsing articleEEATScore string:", error);
        }
    }

    // If it's already an object, handle nested JSON strings
    if (data && typeof data === 'object') {
        // Special case: if there's an analysis field with a JSON string
        if (data.analysis && typeof data.analysis === 'string') {
            try {
                // Try parsing the analysis field as JSON
                const analysisData = JSON.parse(data.analysis);

                // Replace the string with the parsed object
                data.analysis = analysisData;
            } catch (error) {
                console.error("Error parsing analysis field as JSON:", error);
                // Continue with the original object
            }
        }
        return data;
    }

    // If it's a string, try to parse it
    if (typeof data === 'string') {
        try {
            // Clean the string before parsing
            const cleanJsonString = String(data)
                .trim()
                .replace(/^\s*['"]|['"]\s*$/g, '')  // Remove leading/trailing quotes
                .replace(/\\n/g, ' ');              // Replace newlines with spaces

            // Parse the entire thing as a single JSON object
            const parsed = JSON.parse(cleanJsonString);
            return parseEEATData(parsed); // Recursively parse the result
        } catch (error) {
            console.error("Error parsing EEAT data string:", error);
            return null;
        }
    }

    // Return null for undefined or null values
    return null;
};

/**
 * Format EEAT data for the Sidebar component
 * Takes a raw EEAT data string or object and formats it for the Sidebar
 * @param rawData The raw data to format
 * @returns Formatted EEAT data object
 */
export const formatEEATDataForSidebar = (rawData: any) => {
    // First try to parse the data
    const parsedData = parseEEATData(rawData);

    if (!parsedData) {
        return null;
    }

    // Create a properly structured object for the Sidebar component
    return {
        articleEEATScore: {
            analysis: parsedData.analysis || {},
            article: parsedData.article || {},
            summary: parsedData.summary || [],
            suggestions: parsedData.suggestions || []
        }
    };
};